import classroomNoiseMeterWidget from "assets/img/launch/classroom-noise-meter-widget.png";

export const CLASSROOM_TOOL_PAGE_PATHS = [
  "/classroom-noise-meter",
  "/random-group-generator",
  "/wheel-of-names-for-teachers",
  "/classroom-stopwatch",
  "/bell-ringer-timer",
  "/classroom-transition-timer",
  "/attention-getter-timer",
  "/classroom-routine-builder",
  "/daily-agenda-template-for-teachers",
  "/classroom-countdown-clock",
  "/student-participation-randomizer",
  "/classroom-schedule-display",
];

const AUTOMATION_LINK = { to: "/what-is-classroom-automation", label: "What Is Classroom Automation?" };
const CLASSROOMSCREEN_LINK = { to: "/classroomscreen-alternative", label: "ClassroomScreen Alternative" };

export const CLASSROOM_TOOL_PAGES = {
  "/classroom-noise-meter": {
    slug: "classroom-noise-meter",
    h1: "Classroom Noise Meter for Clearer Classroom Routines",
    metaTitle: "Classroom Noise Meter | Lesson Launchpad™",
    metaDescription:
      "Use a classroom noise meter routine with Lesson Launchpad's Sound Meter to make volume expectations visible during group work, transitions, and quiet work.",
    primaryKeyword: "classroom noise meter",
    secondaryKeywords: ["classroom noise level monitor", "classroom noise monitor", "noise meter for classroom"],
    longTailKeywords: ["how to reduce classroom noise", "classroom noise management tool", "classroom noise expectations"],
    hero: {
      eyebrow: "Classroom volume routines",
      value:
        "A classroom noise meter is most useful when students know what the display means before the room gets too loud.",
      cta: "Try Lesson Launchpad™",
    },
    heroActions: [
      { label: "Try Lesson Launchpad™", to: "/auth/register", icon: "fa fa-rocket" },
      { label: "Explore teacher tools", to: "/tools", icon: "fa fa-wrench" },
    ],
    heroCard: {
      image: {
        src: classroomNoiseMeterWidget,
        alt: "Lesson Launchpad classroom noise meter widget showing a Partner Talk activity with waveform volume feedback.",
      },
    },
    sections: [
      {
        id: "noise-drift",
        kicker: "Classroom reality",
        heading: "Noise usually drifts before students realize it",
        body: [
          "Most classroom noise problems do not start with students trying to be disruptive. The volume rises during partner work, cleanup, supply movement, transitions, and the first few minutes after directions.",
          "The hard part is that the teacher becomes the only monitor. You remind the class, wait, remind again, and eventually the routine turns into another interruption.",
        ],
      },
      {
        id: "visible-expectation",
        heading: "Give students a signal they can see before you correct it",
        body: [
          "A noise routine works better when students can compare the room volume to the expectation without waiting for a verbal correction. The display should answer a simple question: does our sound match what we are doing right now?",
          "That routine can work without any software. Name the expected level, practice it during one activity at a time, and reset from the cue before the class drifts too far.",
        ],
        list: {
          variant: "feature",
          items: [
            "Choose the sound expectation before the activity starts.",
            "Put the cue where students naturally look during class.",
            "Use the same language each time, such as silent work, partner talk, group work, or transition.",
            "Reset from the visible cue before repeating another full direction.",
          ],
        },
      },
      {
        id: "sound-meter-support",
        heading: "How Lesson Launchpad's Sound Meter supports the routine",
        body: [
          [
            "In Lesson Launchpad, the product feature is called ",
            { text: "Sound Meter", to: "/auth/register" },
            ". It opens as a floating widget on the Present screen, so a teacher can place it near the agenda, directions, or a timer while students work.",
          ],
          "The teacher starts the microphone manually, chooses the activity preset, and decides whether students should see the monitor. The widget then analyzes volume locally on the device and shows the room's current state with the selected visual style.",
        ],
        list: {
          variant: "feature",
          items: [
            "Use Show monitor when the class should see the volume cue.",
            "Use Don't show monitor when the teacher wants private volume feedback.",
            "Switch presets for Silent Work, Partner Talk, Group Work, Presentation, Transition, or Band Rehearsal.",
            "Choose a visual style such as traffic light, voice scale, waveform, emoji, meter, or minimal.",
          ],
        },
        media: {
          recommendation:
            "Screenshot needed: Present screen with the Sound Meter widget in Show monitor mode, using Voice scale or Traffic light, with the preset set to Group Work or Partner Talk. Show enough agenda or timer context nearby to make the classroom routine clear.",
        },
      },
      {
        id: "teacher-controls",
        heading: "Set the monitor for the activity, not for the whole day",
        body: [
          "A useful volume cue changes with the task. Quiet independent work, partner talk, group work, transitions, presentations, and band rehearsal should not all share the same threshold.",
          "The Sound Meter settings let the teacher adjust warning and too-loud levels, sensitivity, alert delay, microphone input, dark mode, and optional class comparison summaries.",
        ],
        list: {
          items: [
            "Alert sound is optional and off by default.",
            "Live timeline is a session-only view the teacher can turn on when it helps.",
            "Session history is opt-in and stores volume-level summaries, not audio recordings.",
            "Band Rehearsal adds a playing-threshold setting for rehearsal-style tracking.",
          ],
        },
      },
      {
        id: "clear-limits",
        heading: "What it does not do automatically",
        body: [
          "The Sound Meter is a classroom display tool, not an automatic classroom-management system by itself. That distinction matters because teachers should know exactly what to expect before using it live.",
        ],
        list: {
          items: [
            "It does not start the microphone automatically when the widget opens.",
            "It does not change presets from the bell schedule, agenda, timer, or reminders.",
            "It does not record, upload, or replay classroom audio.",
            "It works best when the teacher makes the sound expectation part of a practiced classroom routine.",
          ],
        },
      },
    ],
    finalCta: {
      heading: "Make classroom expectations visible from the Present screen.",
      body:
        "Use Lesson Launchpad to keep routines, agendas, timers, reminders, and tools like Sound Meter in one place during class.",
      actions: [
        { label: "Start Free Trial", to: "/auth/register", icon: "fa fa-rocket" },
        { label: "Compare classroom tools", to: "/classroomscreen-alternative", icon: "fa fa-columns" },
      ],
    },
    faqs: [
      {
        question: "What is a classroom noise meter for teachers?",
        answer:
          "A classroom noise meter is a visual cue that helps students compare the room volume to the expectation for the current activity.",
      },
      {
        question: "What does Lesson Launchpad's Sound Meter actually measure?",
        answer:
          "The Sound Meter uses the device microphone to analyze live volume level in the browser. It does not identify speakers, transcribe audio, or judge whether student talk is appropriate.",
      },
      {
        question: "Does the Sound Meter record classroom audio?",
        answer:
          "No. Audio is analyzed on the device. Lesson Launchpad does not record or upload raw audio. If session history is enabled, only volume-level summaries may be saved.",
      },
      {
        question: "Can students see the Sound Meter?",
        answer:
          "Yes, when the teacher chooses Show monitor. Teachers can also choose Don't show monitor for private feedback.",
      },
      {
        question: "Does the Sound Meter change automatically with timers or agendas?",
        answer:
          "No. The teacher starts the microphone and chooses the preset manually. The widget can sit near timers or agenda content on the Present screen, but it does not automatically change based on those tools.",
      },
    ],
    sidebar: {
      title: "More ways to simplify classroom routines",
      description:
        "Explore tools and ideas for smoother transitions, clearer expectations, and less repetitive teacher management.",
      links: [
        { to: "/classroom-transition-timer", label: "Classroom Transition Timer" },
        { to: "/classroom-routine-builder", label: "Classroom Routine Builder" },
        { to: "/classroom-timer", label: "Classroom Timer" },
        CLASSROOMSCREEN_LINK,
      ],
    },
    bottomRelated: {
      title: "Keep building your classroom routine",
      heading: "Related tools and strategies",
      links: [
        { to: "/classroom-transition-timer", label: "Classroom Transition Timer" },
        { to: "/classroom-routine-builder", label: "Classroom Routine Builder" },
        { to: "/classroom-timer", label: "Classroom Timer" },
      ],
      compare: [CLASSROOMSCREEN_LINK],
      product: [
        AUTOMATION_LINK,
        { to: "/pricing", label: "Pricing" },
      ],
    },
    relatedLinks: [
      AUTOMATION_LINK,
      { to: "/classroom-transition-timer", label: "Classroom Transition Timer" },
      { to: "/classroom-routine-builder", label: "Classroom Routine Builder" },
      CLASSROOMSCREEN_LINK,
    ],
  },
  "/random-group-generator": {
    slug: "random-group-generator",
    h1: "Random Group Generator for Faster, Fairer Student Groups",
    metaTitle: "Random Group Generator | Lesson Launchpad™",
    metaDescription:
      "Create random student groups quickly while supporting participation, equity, and smoother classroom routines.",
    primaryKeyword: "random group generator",
    secondaryKeywords: ["random student groups", "classroom group generator", "student grouping tool"],
    longTailKeywords: ["how to create random student groups", "group students quickly"],
    hero: {
      eyebrow: "Participation Tool",
      value:
        "Create groups without turning the first five minutes of collaboration into negotiation, side conversations, and lost momentum.",
      cta: "Plan faster group routines",
    },
    problem: {
      headline: "Grouping students can take longer than the activity itself should.",
      paragraphs: [
        "You say, 'Find a partner,' and the same students move together. You assign groups by hand, and someone complains. You try to balance ability, behavior, friendships, and absences while the room waits.",
        "The friction is real because grouping is not just logistics. It affects participation, equity, classroom management, and how quickly students begin the actual work.",
      ],
    },
    why: {
      headline: "Why student grouping creates classroom friction",
      items: [
        "Students often default to friends, leaving some classmates out or over-relying on the same partners.",
        "Manual grouping takes teacher attention during a moment when students need clear directions.",
        "Last-minute absences can break carefully planned groups.",
        "Without a routine, grouping becomes a social negotiation instead of an instructional structure.",
      ],
    },
    traditional: {
      headline: "Traditional grouping methods still have a place",
      items: [
        "Teacher-created groups are useful when you need specific academic or behavior supports.",
        "Student choice can increase comfort and buy-in for low-stakes activities.",
        "Numbering off is fast, but it can feel random in a way students do not trust.",
      ],
    },
    better: {
      headline: "Use random groups when speed and fairness matter most",
      paragraphs: [
        "A random group generator works well for quick discussions, review games, lab rotations, station work, and activities where the main goal is broad participation.",
        "The key is to make grouping part of the routine. Students should know where groups appear, how quickly they move, and what they do first once they arrive.",
      ],
    },
    features: [
      "Create groups without repeating the same social patterns every time.",
      "Show groups on the classroom display so students can move without asking you where to go.",
      "Pair groups with an automatic work timer so collaboration starts immediately.",
      "Use reminders to show the first task, group role, or discussion question.",
    ],
    tips: [
      "Tell students why you are randomizing: fairness, new voices, and faster starts.",
      "Keep group size tied to the task. Pairs for accountability, groups of three or four for discussion, larger groups only when roles are clear.",
      "Give a first action before groups move, such as 'Bring your notebook and start question 1.'",
      "Use predictable movement routines so group creation does not become a transition problem.",
      "Save teacher-created groups for moments when randomization would undermine support needs.",
    ],
    connection: [
      [
        "A classroom group generator solves the selection moment. Lesson Launchpad helps with what happens next: display groups, start a timer, show the task, and keep the routine moving.",
      ],
      [
        "For participation routines, also see the ",
        { text: "Wheel of Names for Teachers", to: "/wheel-of-names-for-teachers" },
        " and ",
        { text: "Student Participation Randomizer", to: "/student-participation-randomizer" },
        " pages.",
      ],
    ],
    media: {
      screenshot: "[SCREENSHOT PLACEHOLDER] Ideal screenshot: projected student groups with group numbers, roles, and a first task.",
      gif: "[GIF PLACEHOLDER] Ideal GIF: roster shuffling into random groups, then a group-work timer starting.",
      video: "[VIDEO PLACEHOLDER] Ideal video: teacher creates groups, students move, and the first discussion prompt appears automatically.",
    },
    faqs: [
      {
        question: "How do I create random student groups quickly?",
        answer:
          "Use a random group generator, choose the group size, display the results, and give students a clear first task before they move.",
      },
      {
        question: "Are random groups always the best choice?",
        answer:
          "No. Random groups are best for speed, fairness, and variety. Teacher-created groups are better when specific academic, behavior, or language supports matter.",
      },
      {
        question: "How many students should be in each group?",
        answer:
          "Pairs work well for accountability. Groups of three or four work well for discussion and collaboration. Larger groups need defined roles.",
      },
      {
        question: "How can I prevent group transitions from getting noisy?",
        answer:
          "Display the groups, use a short transition timer, state the expected voice level, and give students something to do immediately when they arrive.",
      },
      {
        question: "Can Lesson Launchpad automate group-work routines?",
        answer:
          "Lesson Launchpad can support group-work routines with displayed directions, timers, reminders, and schedule-based transitions.",
      },
    ],
    relatedLinks: [
      { to: "/student-participation-randomizer", label: "Student Participation Randomizer" },
      { to: "/wheel-of-names-for-teachers", label: "Wheel of Names for Teachers" },
      { to: "/classroom-transition-timer", label: "Classroom Transition Timer" },
      AUTOMATION_LINK,
    ],
  },
  "/wheel-of-names-for-teachers": {
    slug: "wheel-of-names-for-teachers",
    h1: "Wheel of Names for Teachers: Fair Student Selection Without the Drama",
    metaTitle: "Wheel of Names for Teachers | Lesson Launchpad™",
    metaDescription:
      "Use a teacher-friendly wheel of names to support fair student participation, engagement, and calm classroom routines.",
    primaryKeyword: "wheel of names for teachers",
    secondaryKeywords: ["random student picker wheel", "classroom wheel spinner"],
    longTailKeywords: ["random student participation", "fair student selection"],
    hero: {
      eyebrow: "Engagement Tool",
      value:
        "A student picker wheel can make participation feel fair, visible, and routine instead of personal or unpredictable.",
      cta: "Make participation feel fair",
    },
    problem: {
      headline: "Calling on students can feel more loaded than it should.",
      paragraphs: [
        "You want more voices in the room, not just the same three hands. But cold calling can feel tense if students think they were singled out. Volunteers are easier, but they do not always create equitable participation.",
        "A wheel of names gives the selection process a visible structure. The teacher still sets the tone, but the tool helps students understand that participation is part of the routine.",
      ],
    },
    why: {
      headline: "Why participation routines break down",
      items: [
        "The same students often volunteer while quieter students opt out.",
        "Students may perceive teacher selection as personal, even when it is not.",
        "Without wait time and support, random selection can increase anxiety.",
        "Participation tools fail when they are used as a surprise instead of a routine.",
      ],
    },
    traditional: {
      headline: "Traditional participation strategies still matter",
      items: [
        "Popsicle sticks can work, but they are easy to lose and hard to reset across classes.",
        "Calling on raised hands is comfortable, but it can narrow participation.",
        "Turn-and-talk gives rehearsal time, but someone still needs to share out.",
      ],
    },
    better: {
      headline: "Use the wheel as part of a low-stress participation routine",
      paragraphs: [
        "A classroom wheel spinner works best after students have had time to think, write, or discuss. The wheel chooses the speaker, but the routine gives students something ready to say.",
        "Fair does not mean harsh. You can allow passes, pair responses, or 'phone a friend' moments while still making broad participation normal.",
      ],
    },
    features: [
      "Display the picker so students see the process is fair.",
      "Use no-repeat rounds so participation spreads across the class.",
      "Pair selection with a visible prompt or sentence stem.",
      "Use timers for think time before spinning the wheel.",
    ],
    tips: [
      "Give students think time before using the wheel.",
      "Normalize passes for questions that require vulnerability, and use follow-up later.",
      "Remove names after a successful response if you want everyone to participate once.",
      "Use the wheel for low-stakes moments first so students learn it is not a punishment.",
      "Pair random selection with clear routines for listening and responding respectfully.",
    ],
    connection: [
      [
        "A wheel of names helps with selection. Lesson Launchpad helps with the classroom system around it: prompt on screen, think-time countdown, random picker, and transition to the next activity.",
      ],
      [
        "If you are building broader engagement routines, compare this with the ",
        { text: "Student Participation Randomizer", to: "/student-participation-randomizer" },
        " and the ",
        { text: "Random Group Generator", to: "/random-group-generator" },
        ".",
      ],
    ],
    media: {
      screenshot: "[SCREENSHOT PLACEHOLDER] Ideal screenshot: classroom wheel spinner with a discussion prompt and wait-time countdown beside it.",
      gif: "[GIF PLACEHOLDER] Ideal GIF: wheel spinning, selecting a student, then removing the name for a no-repeat round.",
      video: "[VIDEO PLACEHOLDER] Ideal video: teacher gives think time, spins the wheel, and students share with a supportive routine.",
    },
    faqs: [
      {
        question: "What is a wheel of names for teachers?",
        answer:
          "It is a classroom spinner that randomly selects a student name for participation, jobs, turns, or group roles.",
      },
      {
        question: "How do I use a random student picker wheel fairly?",
        answer:
          "Explain the routine, give think time first, use no-repeat rounds when appropriate, and keep the tone supportive.",
      },
      {
        question: "Does random selection increase student anxiety?",
        answer:
          "It can if used as a surprise. It works better when students have preparation time, sentence stems, and clear expectations.",
      },
      {
        question: "Can I use a wheel spinner for classroom jobs?",
        answer:
          "Yes. A wheel can assign jobs, turns, materials roles, discussion speakers, or review game teams.",
      },
      {
        question: "How does this connect to classroom automation?",
        answer:
          "Automation can display the prompt, start wait time, launch the participation routine, and move the class to the next activity without extra clicks.",
      },
    ],
    relatedLinks: [
      { to: "/student-participation-randomizer", label: "Student Participation Randomizer" },
      { to: "/random-group-generator", label: "Random Group Generator" },
      { to: "/classroom-stopwatch", label: "Classroom Stopwatch" },
      CLASSROOMSCREEN_LINK,
    ],
  },
  "/classroom-stopwatch": {
    slug: "classroom-stopwatch",
    h1: "Classroom Stopwatch for Pacing Activities and Transitions",
    metaTitle: "Classroom Stopwatch | Lesson Launchpad™",
    metaDescription:
      "Use a classroom stopwatch to improve pacing, transitions, and time awareness without adding more teacher workload.",
    primaryKeyword: "classroom stopwatch",
    secondaryKeywords: ["stopwatch for teachers", "classroom time management"],
    longTailKeywords: ["classroom pacing tool", "track classroom activity time"],
    hero: {
      eyebrow: "Time Management Tool",
      value:
        "A stopwatch helps you see how long classroom routines actually take, then tighten them without rushing students.",
      cta: "Improve classroom pacing",
    },
    problem: {
      headline: "Some classroom time loss is hard to see until you measure it.",
      paragraphs: [
        "Passing out materials feels like one minute. It might be four. Partner discussion feels short. It might be twelve. The stopwatch is useful because it turns a vague feeling into something you can adjust.",
        "Teachers do not need another clock to stare at. They need practical time awareness that supports better pacing, smoother transitions, and more confident decisions during class.",
      ],
    },
    why: {
      headline: "Why pacing is difficult during a live lesson",
      items: [
        "Teachers are monitoring understanding, behavior, materials, and time at once.",
        "Small delays stack up across multiple transitions.",
        "Students may not feel urgency unless time is visible.",
        "A wall clock tells the time of day, but not how long the current routine has taken.",
      ],
    },
    traditional: {
      headline: "Traditional timing approaches are often invisible to students",
      items: [
        "Checking the wall clock helps the teacher but gives students little feedback.",
        "Phone timers work, but they are not always appropriate or visible in class.",
        "Mental estimates are fast, but they are rarely accurate on busy days.",
      ],
    },
    better: {
      headline: "Use a classroom stopwatch to learn and improve routines",
      paragraphs: [
        "A stopwatch is especially helpful when you are diagnosing a routine: how long does cleanup take, how long does attendance take, how long does the first problem actually take?",
        "Once you know the real timing, you can build a better routine with countdowns, reminders, and schedule-based transitions.",
      ],
    },
    features: [
      "Track how long repeated routines actually take.",
      "Compare class periods without relying on memory.",
      "Use visible elapsed time to support student independence.",
      "Turn stopwatch data into better timers and automated routines.",
    ],
    tips: [
      "Time one routine for a week before changing it. Patterns are more useful than one rough day.",
      "Use the stopwatch privately for diagnosis and publicly for challenges only when it supports the culture you want.",
      "Separate transition time from work time so you know where minutes are going.",
      "After measuring, replace repeated stopwatch use with a predictable countdown timer.",
      "Ask students what slowed the routine down. They often know the friction points.",
    ],
    connection: [
      [
        "A stopwatch helps you discover the real length of a routine. ",
        { text: "Classroom automation", to: "/what-is-classroom-automation" },
        " helps you turn that discovery into a repeatable system.",
      ],
      [
        "When you are ready to move from measuring to improving, use a ",
        { text: "Classroom Countdown Clock", to: "/classroom-countdown-clock" },
        " or ",
        { text: "Classroom Transition Timer", to: "/classroom-transition-timer" },
        " to make the timing visible to students.",
      ],
    ],
    media: {
      screenshot: "[SCREENSHOT PLACEHOLDER] Ideal screenshot: stopwatch tracking cleanup time with a note area for routine observations.",
      gif: "[GIF PLACEHOLDER] Ideal GIF: stopwatch running during a materials transition, then converting to a two-minute countdown routine.",
      video: "[VIDEO PLACEHOLDER] Ideal video: teacher measures a routine, adjusts the timing, then automates the improved version.",
    },
    faqs: [
      {
        question: "How can teachers use a classroom stopwatch?",
        answer:
          "Teachers can time transitions, cleanup, student work, procedures, presentations, or any routine where elapsed time matters.",
      },
      {
        question: "Is a stopwatch better than a countdown timer?",
        answer:
          "A stopwatch is better for measuring how long something takes. A countdown timer is better for setting a clear limit before the activity begins.",
      },
      {
        question: "Should students see the stopwatch?",
        answer:
          "Sometimes. Public elapsed time can help with awareness, but private measurement is better when you are diagnosing a routine without adding pressure.",
      },
      {
        question: "How does timing routines save instructional time?",
        answer:
          "Measured routines reveal where minutes are being lost. Once you know the pattern, you can adjust directions, materials, countdowns, and transitions.",
      },
      {
        question: "Can Lesson Launchpad automate timing after I measure a routine?",
        answer:
          "Yes. Once you know the timing that works, you can build timers and reminders into an automated classroom routine.",
      },
    ],
    relatedLinks: [
      { to: "/classroom-countdown-clock", label: "Classroom Countdown Clock" },
      { to: "/classroom-transition-timer", label: "Classroom Transition Timer" },
      { to: "/bell-ringer-timer", label: "Bell Ringer Timer" },
      AUTOMATION_LINK,
    ],
  },
  "/bell-ringer-timer": {
    slug: "bell-ringer-timer",
    h1: "Bell Ringer Timer for Faster Starts to Class",
    metaTitle: "Bell Ringer Timer | Lesson Launchpad™",
    metaDescription:
      "Use a bell ringer timer to start class with less reminding, clearer expectations, and more instructional time.",
    primaryKeyword: "bell ringer timer",
    secondaryKeywords: ["classroom bell work timer", "warm up timer classroom"],
    longTailKeywords: ["start class quickly", "bell work routine timer"],
    hero: {
      eyebrow: "Classroom Starts",
      value:
        "The first three minutes of class often decide whether the room settles into learning or drifts into reminders.",
      cta: "Start class with structure",
    },
    problem: {
      headline: "Slow starts quietly drain the rest of the period.",
      paragraphs: [
        "Students enter, ask what they need, sharpen pencils, talk across the room, and wait for you to repeat the same directions. By the time the warm-up begins, the class has already learned that the first few minutes are flexible.",
        "A bell ringer timer gives the start of class a shape. Students see the task, the time, and the expectation before you have to narrate all of it.",
      ],
    },
    why: {
      headline: "Why bell work routines are hard to keep consistent",
      items: [
        "Teachers are often handling attendance, hallway conversations, materials, and late arrivals at the same time.",
        "Students need the task, materials, and time limit visible immediately.",
        "If the timer starts late, students learn the routine is negotiable.",
        "Repeating directions to late students slows everyone else down.",
      ],
    },
    traditional: {
      headline: "Traditional bell work works when the routine is protected",
      items: [
        "A slide on the board helps, but it still needs to be updated and displayed on time.",
        "Verbal reminders help late students, but they interrupt students who already started.",
        "A timer tab works, but starting it manually becomes one more beginning-of-class task.",
      ],
    },
    better: {
      headline: "A bell ringer timer should begin before students ask what to do",
      paragraphs: [
        "The best warm-up routines make the first action obvious: open the notebook, answer the prompt, solve the problem, reflect on yesterday, or review vocabulary.",
        "When the timer and agenda appear automatically, you can greet students, take attendance, and handle small issues without sacrificing the start of instruction.",
      ],
    },
    features: [
      "Display bell work directions and a visible warm-up timer.",
      "Start the timer at the same time every period.",
      "Show reminders for materials, notebooks, or expected voice level.",
      "Transition automatically from bell work into the next lesson segment.",
    ],
    tips: [
      "Keep bell ringers short enough to finish, usually three to seven minutes.",
      "Use the same entry routine daily, even when the task changes.",
      "Place directions, materials, and time remaining in one visible location.",
      "Have an early-finisher instruction ready so fast students do not restart chatter.",
      "Review the bell ringer quickly so students believe the work matters.",
    ],
    connection: [
      [
        "A bell ringer timer solves the clock problem. Lesson Launchpad goes further by automatically showing the prompt, starting the timer, and moving the class into the next activity.",
      ],
      [
        "For a complete start-of-class workflow, connect this with a ",
        { text: "Daily Agenda Template for Teachers", to: "/daily-agenda-template-for-teachers" },
        " and the ",
        { text: "Classroom Routine Builder", to: "/classroom-routine-builder" },
        ".",
      ],
    ],
    media: {
      screenshot: "[SCREENSHOT PLACEHOLDER] Ideal screenshot: bell ringer prompt, five-minute timer, materials list, and today's agenda on one classroom display.",
      gif: "[GIF PLACEHOLDER] Ideal GIF: class period begins, bell work appears, timer starts automatically.",
      video: "[VIDEO PLACEHOLDER] Ideal video: students enter, see the warm-up, begin work while the teacher greets students and takes attendance.",
    },
    faqs: [
      {
        question: "What is a bell ringer timer?",
        answer:
          "A bell ringer timer is a visible countdown used at the start of class to give students a clear time limit for warm-up work.",
      },
      {
        question: "How long should a bell ringer be?",
        answer:
          "Most bell ringers work best at three to seven minutes, depending on grade level, task complexity, and transition needs.",
      },
      {
        question: "What should I put on the screen with a bell work timer?",
        answer:
          "Show the prompt, materials needed, time remaining, and what students should do when they finish.",
      },
      {
        question: "How do I get students to start bell work without reminders?",
        answer:
          "Make the routine predictable, display the task before students arrive, start the timer consistently, and hold the work accountable in a quick way.",
      },
      {
        question: "Can Lesson Launchpad start bell ringer timers automatically?",
        answer:
          "Yes. Lesson Launchpad can schedule warm-up timers and directions to appear automatically at the start of each class period.",
      },
    ],
    relatedLinks: [
      { to: "/daily-agenda-template-for-teachers", label: "Daily Agenda Template for Teachers" },
      { to: "/classroom-routine-builder", label: "Classroom Routine Builder" },
      { to: "/classroom-schedule-display", label: "Classroom Schedule Display" },
      AUTOMATION_LINK,
    ],
  },
  "/classroom-transition-timer": {
    slug: "classroom-transition-timer",
    h1: "Classroom Transition Timer for Smoother Activity Changes",
    metaTitle: "Classroom Transition Timer | Lesson Launchpad™",
    metaDescription:
      "Use a classroom transition timer to reduce repeated directions, protect instructional time, and automate smoother routines.",
    primaryKeyword: "classroom transition timer",
    secondaryKeywords: ["transition timer for students", "classroom transitions"],
    longTailKeywords: ["improve classroom transitions", "transition timer classroom management"],
    hero: {
      eyebrow: "Classroom Automation Tool",
      value:
        "Transitions can quietly consume 10-15 minutes of instructional time every day. A timer helps, but an automated routine helps even more.",
      cta: "Automate smoother transitions",
    },
    problem: {
      headline: "Transitions are where good lessons often lose momentum.",
      paragraphs: [
        "The activity ends. Students need to clean up, move, take out materials, shift attention, and understand what comes next. Meanwhile, you are changing slides, starting a timer, answering questions, and trying to keep the room calm.",
        "The challenge is not that students cannot transition. It is that transitions ask everyone to change behavior at the same moment, and the teacher is usually the only system holding it together.",
      ],
    },
    why: {
      headline: "Why classroom transitions take longer than planned",
      items: [
        "Students do not always know how much time they have or what the finished transition should look like.",
        "The next task is often not visible until after the teacher changes the display.",
        "Verbal directions compete with movement, noise, and materials.",
        "Timers help only if they start at the right moment every time.",
      ],
    },
    traditional: {
      headline: "Traditional transition strategies are useful but manual",
      items: [
        "Countdowns create urgency, but starting them late weakens the routine.",
        "Transition music gives an auditory cue, but someone has to start and stop it.",
        "Call-and-response attention getters reset the room, but they depend on teacher timing.",
      ],
    },
    better: {
      headline: "The strongest transition timer is part of an automated sequence",
      paragraphs: [
        "A transition timer should not live alone in a browser tab. It should appear with the next direction, expected materials, voice level, and what students do when time ends.",
        "When that sequence runs automatically, students begin responding to the routine instead of waiting for you to orchestrate every step.",
      ],
    },
    features: [
      "Start transition timers automatically at scheduled moments.",
      "Pair countdowns with music, agenda updates, and reminders.",
      "Show what students should do before, during, and after the transition.",
      "Build consistent transition routines across every class period.",
    ],
    tips: [
      "Teach the exact finished state: seated, materials out, voices off, eyes forward, or whatever your room needs.",
      "Use short timers. Long transition windows often invite extra talking.",
      "Add an auditory cue, such as music or a tone, when students need to move.",
      "Display the next task before the timer ends so students are not waiting for instructions.",
      "Practice transitions when the class is calm, not only after a difficult moment.",
    ],
    connection: [
      [
        "This is where Lesson Launchpad's classroom automation positioning matters most. A standalone transition timer can help one moment. Lesson Launchpad can run the whole sequence: countdown starts, music plays, the agenda changes, and the next materials appear.",
      ],
      [
        "For nearby workflows, see ",
        { text: "Classroom Countdown Clock", to: "/classroom-countdown-clock" },
        ", ",
        { text: "Classroom Routine Builder", to: "/classroom-routine-builder" },
        ", and ",
        { text: "ClassroomScreen Alternative", to: "/classroomscreen-alternative" },
        ".",
      ],
    ],
    media: {
      screenshot: "[SCREENSHOT PLACEHOLDER] Ideal screenshot: transition countdown with next activity, materials, and expected voice level.",
      gif: "[GIF PLACEHOLDER] Ideal GIF: current activity ends, transition timer appears, music cue starts, next agenda item loads.",
      video: "[VIDEO PLACEHOLDER] Ideal video: transition automation triggering without teacher clicks while students move to the next routine.",
    },
    faqs: [
      {
        question: "What is a classroom transition timer?",
        answer:
          "A classroom transition timer is a visible countdown that shows students how much time they have to move from one activity, space, or routine to the next.",
      },
      {
        question: "How long should classroom transitions take?",
        answer:
          "It depends on the task, but many classroom transitions work best when they are short, explicitly taught, and paired with a visible finished state.",
      },
      {
        question: "How do transition timers improve classroom management?",
        answer:
          "They make time and expectations visible, reducing repeated directions and helping students self-manage during movement.",
      },
      {
        question: "Should I use music with a transition timer?",
        answer:
          "Music can be helpful when students need an auditory cue. Keep it consistent and make sure students know what should happen before the music ends.",
      },
      {
        question: "Can Lesson Launchpad automate classroom transitions?",
        answer:
          "Yes. Lesson Launchpad can automate timers, music, reminders, agenda changes, and class changes so transitions run as a complete routine.",
      },
    ],
    relatedLinks: [
      AUTOMATION_LINK,
      { to: "/classroom-countdown-clock", label: "Classroom Countdown Clock" },
      { to: "/classroom-routine-builder", label: "Classroom Routine Builder" },
      CLASSROOMSCREEN_LINK,
    ],
  },
  "/attention-getter-timer": {
    slug: "attention-getter-timer",
    h1: "Classroom Attention Getter Timer for Consistent Signals",
    metaTitle: "Classroom Attention Getter | Lesson Launchpad™",
    metaDescription:
      "Build a consistent classroom attention signal with timed routines, visual cues, and fewer repeated reminders.",
    primaryKeyword: "classroom attention getter",
    secondaryKeywords: ["attention signal classroom", "get students attention quickly"],
    longTailKeywords: ["attention getter timer", "classroom attention routine"],
    hero: {
      eyebrow: "Classroom Consistency",
      value:
        "Getting attention quickly is easier when students recognize the signal, the expectation, and the time frame every time.",
      cta: "Create a calmer signal routine",
    },
    problem: {
      headline: "Attention signals fail when they feel optional.",
      paragraphs: [
        "You raise your hand, count down, clap a pattern, ring a bell, or say the phrase your class practiced in August. Some students respond. Others finish their sentence. A few do not hear it at all.",
        "The frustration is familiar: you are not trying to be louder than the room. You are trying to return everyone to a shared focus without spending your energy on repeated correction.",
      ],
    },
    why: {
      headline: "Why getting attention takes longer than it should",
      items: [
        "Students need a clear signal and a clear expected response.",
        "The signal is less effective when it changes from day to day.",
        "Attention cues compete with movement, devices, materials, and peer conversation.",
        "Without a time frame, students do not know how quickly they are expected to respond.",
      ],
    },
    traditional: {
      headline: "Attention getters work best when they are practiced",
      items: [
        "Call-and-response cues create participation, but they need consistency.",
        "Countdowns provide urgency, but they can become background noise if overused.",
        "Bells or chimes are clear, but students still need to know what to do next.",
      ],
    },
    better: {
      headline: "Pair the signal with a visible timed expectation",
      paragraphs: [
        "An attention getter timer gives students a short window to finish the transition back to listening: voices off, eyes up, materials still, or whatever response you teach.",
        "The timer is not a threat. It is a shared cue that helps students move from social energy back into classroom focus.",
      ],
    },
    features: [
      "Display the attention signal and expected response.",
      "Use a short countdown for returning to focus.",
      "Pair the cue with a visual reminder or sound.",
      "Make the same attention routine appear during predictable transition points.",
    ],
    tips: [
      "Choose one primary attention signal and practice it until students can explain the response.",
      "Make the expected body language visible: voice off, hands still, eyes on speaker.",
      "Use a short timer, often five to fifteen seconds, for routine attention resets.",
      "Avoid turning the signal into a lecture. Reset, acknowledge, and continue.",
      "Re-teach the routine after breaks, schedule changes, or substitute days.",
    ],
    connection: [
      [
        "A classroom attention getter solves the reset moment. Lesson Launchpad can place that reset inside a larger routine, especially before transitions, discussions, and independent work.",
      ],
      [
        "For related systems, see the ",
        { text: "Classroom Noise Meter", to: "/classroom-noise-meter" },
        ", ",
        { text: "Classroom Transition Timer", to: "/classroom-transition-timer" },
        ", and ",
        { text: "What Is Classroom Automation?", to: "/what-is-classroom-automation" },
        " guide.",
      ],
    ],
    media: {
      screenshot: "[SCREENSHOT PLACEHOLDER] Ideal screenshot: attention signal display with a ten-second focus countdown and expected student response.",
      gif: "[GIF PLACEHOLDER] Ideal GIF: attention cue appears, countdown runs, display changes to next instruction.",
      video: "[VIDEO PLACEHOLDER] Ideal video: teacher uses a consistent signal while the screen supports the reset and next step.",
    },
    faqs: [
      {
        question: "What is a classroom attention getter?",
        answer:
          "A classroom attention getter is a signal that tells students to stop, listen, and return focus to the teacher or display.",
      },
      {
        question: "How can I get students' attention quickly?",
        answer:
          "Use one consistent signal, teach the expected response, practice it, and pair it with a short visible countdown.",
      },
      {
        question: "Should attention getters be verbal or visual?",
        answer:
          "Both can work. Many classrooms benefit from a verbal or auditory cue supported by a visual display of what students should do next.",
      },
      {
        question: "Why do attention signals stop working?",
        answer:
          "They often stop working when the response is not practiced, the signal changes too often, or students do not see a clear next action.",
      },
      {
        question: "Can automation help attention routines?",
        answer:
          "Yes. Automation can display attention cues, start reset timers, and move directly into the next instruction at predictable moments.",
      },
    ],
    relatedLinks: [
      { to: "/classroom-noise-meter", label: "Classroom Noise Meter" },
      { to: "/classroom-transition-timer", label: "Classroom Transition Timer" },
      { to: "/classroom-routine-builder", label: "Classroom Routine Builder" },
      AUTOMATION_LINK,
    ],
  },
  "/classroom-routine-builder": {
    slug: "classroom-routine-builder",
    h1: "Classroom Routine Builder for Consistent Teaching Days",
    metaTitle: "Classroom Routine Builder | Lesson Launchpad™",
    metaDescription:
      "Build classroom routines that connect agendas, timers, reminders, schedules, and transitions with less daily effort.",
    primaryKeyword: "classroom routines",
    secondaryKeywords: ["classroom routine tool", "classroom routine builder"],
    longTailKeywords: ["build classroom routines", "automate classroom routines", "daily classroom routine software"],
    hero: {
      eyebrow: "Strategic Classroom Automation Page",
      value:
        "Teaching is hard enough. Your classroom routines should not require constant effort every period of every day.",
      cta: "Build routines that run",
    },
    problem: {
      headline: "The hard part is not knowing what routines should exist.",
      paragraphs: [
        "Most teachers already know the routines they want: bell work, attendance, agenda review, mini lesson, group work, cleanup, dismissal. The hard part is making those routines happen consistently while teaching real students in a real room.",
        "A classroom routine builder should reduce mental load. It should help your day feel more predictable without making you operate another complicated system.",
      ],
    },
    why: {
      headline: "Why classroom routines become inconsistent",
      items: [
        "Teachers are interrupted constantly by questions, behavior needs, announcements, and schedule changes.",
        "Routines often live in the teacher's memory instead of on a visible system.",
        "Different class periods start from different energy levels.",
        "Manual tools require another click exactly when the teacher is already busy.",
      ],
    },
    traditional: {
      headline: "Traditional routine tools can organize the plan but not run it",
      items: [
        "Lesson plans describe the routine, but students cannot see them during class.",
        "Slides show agendas, but someone has to update and advance them.",
        "Timers and reminders help, but each one must be started manually unless they are connected.",
      ],
    },
    better: {
      headline: "A classroom routine builder connects the pieces",
      paragraphs: [
        "The routine is not just a list. It is a sequence: display the warm-up, start the timer, show the reminder, play the transition cue, update the agenda, move to the next class.",
        "When those pieces are connected, the classroom becomes more predictable for students and less demanding for the teacher.",
      ],
    },
    features: [
      "Create routines for arrival, bell work, instruction, group work, transitions, cleanup, and dismissal.",
      "Connect timers, agendas, reminders, music, countdowns, and class changes.",
      "Run routines by schedule so they do not depend on teacher memory.",
      "Keep students oriented with what is happening now, what comes next, and how much time remains.",
    ],
    tips: [
      "Start with one routine that causes daily friction instead of redesigning the whole day at once.",
      "Write the routine as student actions, not teacher hopes.",
      "Make the first step visible before students need to ask about it.",
      "Attach a time cue to any routine that regularly drifts.",
      "Review routines after a week and adjust the part that caused the most repeated reminders.",
    ],
    connection: [
      [
        "This page sits closest to Lesson Launchpad's core promise: classroom automation for teachers. You build the classroom routine once, then the system helps execute it with timers, reminders, agendas, music, and schedule changes.",
      ],
      [
        "To build a complete routine, connect this with the ",
        { text: "Daily Agenda Template", to: "/daily-agenda-template-for-teachers" },
        ", ",
        { text: "Classroom Schedule Display", to: "/classroom-schedule-display" },
        ", and ",
        { text: "Bell Ringer Timer", to: "/bell-ringer-timer" },
        ".",
      ],
    ],
    media: {
      screenshot: "[SCREENSHOT PLACEHOLDER] Ideal screenshot: routine builder timeline with bell work, timer, reminder, transition music, and agenda update events.",
      gif: "[GIF PLACEHOLDER] Ideal GIF: teacher adds routine steps, then present screen updates as each step runs.",
      video: "[VIDEO PLACEHOLDER] Ideal video: complete daily routine running automatically from class start through dismissal.",
    },
    faqs: [
      {
        question: "What is a classroom routine builder?",
        answer:
          "A classroom routine builder helps teachers plan and run repeatable classroom procedures such as bell work, transitions, cleanup, and dismissal.",
      },
      {
        question: "What routines should teachers automate first?",
        answer:
          "Start with the routine that creates the most repeated reminders, often bell work, transitions, cleanup, or end-of-class reminders.",
      },
      {
        question: "How do routines improve classroom management?",
        answer:
          "Consistent routines reduce uncertainty. Students know what to do, what comes next, and how much time they have without waiting for repeated directions.",
      },
      {
        question: "Can classroom routines be flexible?",
        answer:
          "Yes. Strong routines create structure, but teachers can still adjust timing, skip steps, or respond to what students need.",
      },
      {
        question: "How is Lesson Launchpad different from a routine checklist?",
        answer:
          "A checklist reminds the teacher what to do. Lesson Launchpad can run parts of the routine automatically on the classroom display.",
      },
    ],
    relatedLinks: [
      AUTOMATION_LINK,
      { to: "/daily-agenda-template-for-teachers", label: "Daily Agenda Template" },
      { to: "/classroom-schedule-display", label: "Classroom Schedule Display" },
      { to: "/bell-ringer-timer", label: "Bell Ringer Timer" },
    ],
  },
  "/daily-agenda-template-for-teachers": {
    slug: "daily-agenda-template-for-teachers",
    h1: "Daily Agenda Template for Teachers That Students Actually Use",
    metaTitle: "Daily Agenda for Teachers | Lesson Launchpad™",
    metaDescription:
      "Create a classroom agenda that improves expectations, routines, planning, and student independence throughout the day.",
    primaryKeyword: "classroom agenda",
    secondaryKeywords: ["daily agenda for teachers", "classroom agenda template"],
    longTailKeywords: ["daily classroom agenda template", "teacher agenda display"],
    hero: {
      eyebrow: "Classroom Expectations",
      value:
        "A daily agenda is more than a list on the board. It is the classroom's shared map for what is happening now and what comes next.",
      cta: "Make expectations visible",
    },
    problem: {
      headline: "Students ask what we are doing when the agenda is not doing its job.",
      paragraphs: [
        "A classroom agenda can prevent repeated questions, but only if it is visible, current, and easy to understand. When the agenda is buried in a slide deck or out of date by second period, students stop trusting it.",
        "Teachers need an agenda system that supports planning and live classroom management, not just a pretty template for the first day of school.",
      ],
    },
    why: {
      headline: "Why classroom agendas get ignored",
      items: [
        "The agenda is not updated when plans shift.",
        "Students cannot tell which part of the agenda is happening now.",
        "Materials, time limits, and directions live somewhere else.",
        "The agenda depends on the teacher remembering to switch screens.",
      ],
    },
    traditional: {
      headline: "Traditional agenda templates are a useful starting point",
      items: [
        "Board agendas are simple, but they can become static background text.",
        "Slide agendas look polished, but they require constant editing and advancing.",
        "Planner agendas help the teacher plan, but students need a live version during class.",
      ],
    },
    better: {
      headline: "A better classroom agenda updates with the routine",
      paragraphs: [
        "The strongest agenda shows the current activity, what comes next, how long students have, and any materials or reminders they need.",
        "When the agenda updates automatically, students learn to look at the display before asking the teacher. That small habit reduces repeated directions and supports independence.",
      ],
    },
    features: [
      "Display today's plan in student-friendly language.",
      "Highlight the current agenda item during the lesson.",
      "Pair agenda items with timers, reminders, and materials.",
      "Update agendas by class period so each group sees the right plan.",
    ],
    tips: [
      "Use verbs students can act on: solve, read, discuss, revise, submit.",
      "Keep the agenda short enough to scan from across the room.",
      "Add time estimates only when they help students manage attention.",
      "Include an early-finisher direction for independent work blocks.",
      "Review the agenda briefly at the start, then point back to it instead of repeating directions.",
    ],
    connection: [
      [
        "A daily agenda template helps students know the plan. Lesson Launchpad can make that plan active by connecting agenda items to timers, reminders, countdowns, and class changes.",
      ],
      [
        "For stronger daily structure, pair this with the ",
        { text: "Classroom Schedule Display", to: "/classroom-schedule-display" },
        ", ",
        { text: "Bell Ringer Timer", to: "/bell-ringer-timer" },
        ", and ",
        { text: "Classroom Routine Builder", to: "/classroom-routine-builder" },
        ".",
      ],
    ],
    media: {
      screenshot: "[SCREENSHOT PLACEHOLDER] Ideal screenshot: daily agenda with current activity highlighted, timer, materials, and next step.",
      gif: "[GIF PLACEHOLDER] Ideal GIF: agenda automatically advances from bell work to lesson to group work.",
      video: "[VIDEO PLACEHOLDER] Ideal video: teacher updates one plan and the classroom display changes for each period automatically.",
    },
    faqs: [
      {
        question: "What should be included in a classroom agenda?",
        answer:
          "Include the current activity, next steps, materials, time expectations, and any reminders students need to work independently.",
      },
      {
        question: "How is a daily agenda different from a lesson plan?",
        answer:
          "A lesson plan is for the teacher. A daily agenda is a student-facing version of the plan that helps the class understand what is happening.",
      },
      {
        question: "How can I make students use the agenda?",
        answer:
          "Keep it current, reference it consistently, and include information students actually need, such as time remaining and materials.",
      },
      {
        question: "Should the agenda stay on screen all class?",
        answer:
          "It can, but it works best when it changes with the routine so the current task remains clear.",
      },
      {
        question: "Can Lesson Launchpad update classroom agendas automatically?",
        answer:
          "Yes. Lesson Launchpad can display period-specific agendas and update them alongside timers, reminders, and scheduled routines.",
      },
    ],
    relatedLinks: [
      { to: "/classroom-routine-builder", label: "Classroom Routine Builder" },
      { to: "/classroom-schedule-display", label: "Classroom Schedule Display" },
      { to: "/bell-ringer-timer", label: "Bell Ringer Timer" },
      AUTOMATION_LINK,
    ],
  },
  "/classroom-countdown-clock": {
    slug: "classroom-countdown-clock",
    h1: "Classroom Countdown Clock for Transitions and Student Independence",
    metaTitle: "Classroom Countdown Clock | Lesson Launchpad™",
    metaDescription:
      "Use a classroom countdown clock to make time visible for transitions, work blocks, cleanup, and independent routines.",
    primaryKeyword: "classroom countdown clock",
    secondaryKeywords: ["classroom countdown timer", "visual countdown classroom"],
    longTailKeywords: ["visual timer for classroom transitions", "classroom countdown display"],
    hero: {
      eyebrow: "Visual Time Tool",
      value:
        "A countdown clock helps students answer the question they ask all day: how much time do I have left?",
      cta: "Make time visible",
    },
    problem: {
      headline: "Students manage time better when they can see it.",
      paragraphs: [
        "Without a visible countdown, time is mostly a teacher concern. Students ask how much time is left, linger during cleanup, or feel surprised when the activity ends.",
        "A classroom countdown clock turns time into shared information. Students can pace themselves, transition sooner, and rely less on repeated verbal reminders.",
      ],
    },
    why: {
      headline: "Why visual countdowns support independence",
      items: [
        "Students can check the clock without interrupting the teacher.",
        "A visible endpoint reduces uncertainty during work time.",
        "Transitions feel less sudden when the remaining time is clear.",
        "Countdowns make routines concrete for students who struggle with time awareness.",
      ],
    },
    traditional: {
      headline: "Traditional time reminders fade quickly",
      items: [
        "Verbal time warnings are helpful, but students may miss them.",
        "Wall clocks show the time of day, not the time left in the task.",
        "Online countdown tabs work, but they are disconnected from the rest of the lesson.",
      ],
    },
    better: {
      headline: "A countdown clock works best beside the task",
      paragraphs: [
        "The countdown should appear with the activity name, expectation, and next step. Time alone is less useful than time connected to action.",
        "When countdowns become part of the routine, students start managing themselves before you have to intervene.",
      ],
    },
    features: [
      "Show time remaining for work blocks, tests, cleanup, and dismissal.",
      "Use warning reminders before the countdown ends.",
      "Pair countdowns with agenda changes and transition cues.",
      "Schedule recurring countdowns so they appear without manual setup.",
    ],
    tips: [
      "Use countdowns for tasks where time affects behavior or pacing.",
      "Add a one-minute warning for activities that require cleanup or submission.",
      "Avoid overusing countdowns for every tiny task or students may tune them out.",
      "Pair the clock with what students should do when time ends.",
      "Keep the display large enough to read from the back of the room.",
    ],
    connection: [
      [
        "A classroom countdown clock is useful on its own. Lesson Launchpad makes it stronger by starting countdowns automatically when your routine reaches that moment.",
      ],
      [
        "For transition-heavy routines, see the ",
        { text: "Classroom Transition Timer", to: "/classroom-transition-timer" },
        " and ",
        { text: "Classroom Routine Builder", to: "/classroom-routine-builder" },
        ".",
      ],
    ],
    media: {
      screenshot: "[SCREENSHOT PLACEHOLDER] Ideal screenshot: large visual countdown with activity name, next step, and one-minute warning.",
      gif: "[GIF PLACEHOLDER] Ideal GIF: countdown starts automatically, warning appears, then display advances to cleanup.",
      video: "[VIDEO PLACEHOLDER] Ideal video: students use a visible countdown to finish work and transition independently.",
    },
    faqs: [
      {
        question: "What is a classroom countdown clock?",
        answer:
          "A classroom countdown clock is a visual timer that shows students exactly how much time remains for an activity or transition.",
      },
      {
        question: "When should teachers use a countdown timer?",
        answer:
          "Use countdowns for transitions, independent work, tests, cleanup, stations, warm-ups, dismissal, and any routine where time clarity helps.",
      },
      {
        question: "Do visual countdowns help classroom management?",
        answer:
          "Yes. They reduce uncertainty, repeated questions, and surprise endings by making time expectations visible.",
      },
      {
        question: "What should happen when the countdown ends?",
        answer:
          "Students should already know the next action, such as submit, clean up, return to seats, or begin the next agenda item.",
      },
      {
        question: "Can Lesson Launchpad schedule countdown clocks?",
        answer:
          "Yes. Lesson Launchpad can schedule countdowns as part of automated classroom routines.",
      },
    ],
    relatedLinks: [
      { to: "/classroom-transition-timer", label: "Classroom Transition Timer" },
      { to: "/classroom-stopwatch", label: "Classroom Stopwatch" },
      { to: "/bell-ringer-timer", label: "Bell Ringer Timer" },
      AUTOMATION_LINK,
    ],
  },
  "/student-participation-randomizer": {
    slug: "student-participation-randomizer",
    h1: "Student Participation Randomizer for More Equitable Classroom Discussion",
    metaTitle: "Student Participation Randomizer | Lesson Launchpad™",
    metaDescription:
      "Use a student participation randomizer to broaden classroom voices while keeping participation supportive and fair.",
    primaryKeyword: "student participation randomizer",
    secondaryKeywords: ["random student selector", "participation tool classroom"],
    longTailKeywords: ["fair student participation", "random student discussion tool"],
    hero: {
      eyebrow: "Equity and Engagement",
      value:
        "Participation should not depend on who raises a hand fastest or who feels most comfortable taking up space.",
      cta: "Broaden participation",
    },
    problem: {
      headline: "The same voices can carry the room without anyone meaning for that to happen.",
      paragraphs: [
        "In many classrooms, volunteers are reliable, quiet students stay quiet, and participation grades become fuzzy. You want more balanced engagement, but you also do not want random selection to feel like a gotcha.",
        "A student participation randomizer can help when it is paired with wait time, clear prompts, and a classroom culture where mistakes are part of learning.",
      ],
    },
    why: {
      headline: "Why participation is not automatically equitable",
      items: [
        "Confidence, processing speed, language, and peer dynamics all affect who volunteers.",
        "Teachers may unintentionally call on familiar voices more often.",
        "Students need time to prepare before being asked to speak publicly.",
        "Participation tools need routines, not surprises.",
      ],
    },
    traditional: {
      headline: "Traditional participation systems each solve part of the problem",
      items: [
        "Hand raising protects student choice, but it can narrow the discussion.",
        "Cold calling broadens participation, but it can feel stressful without support.",
        "Participation trackers create data, but they add teacher workload during discussion.",
      ],
    },
    better: {
      headline: "Randomize participation after students have something to say",
      paragraphs: [
        "The randomizer should come after thinking, writing, partner rehearsal, or small-group talk. That way selection expands voice without making students feel exposed.",
        "A fair system also includes humane options: pass and return, choose a partner's idea, or read from written notes.",
      ],
    },
    features: [
      "Select students fairly without relying only on volunteers.",
      "Avoid repeat selection until everyone has had a chance.",
      "Display the prompt and sentence stem with the selected name.",
      "Use timers for think time before randomizing.",
    ],
    tips: [
      "Use random selection for questions students have had time to prepare.",
      "Track who has participated across a round rather than across one high-pressure question.",
      "Let students share a partner's idea to reduce pressure and improve listening.",
      "Use sentence stems for academic discussion.",
      "Debrief the routine: students should understand that participation is about inclusion, not embarrassment.",
    ],
    connection: [
      [
        "A random student selector helps distribute turns. Lesson Launchpad helps embed that selection inside a routine: prompt, think timer, selection, response, and transition.",
      ],
      [
        "For adjacent engagement tools, see the ",
        { text: "Wheel of Names for Teachers", to: "/wheel-of-names-for-teachers" },
        " and ",
        { text: "Random Group Generator", to: "/random-group-generator" },
        ".",
      ],
    ],
    media: {
      screenshot: "[SCREENSHOT PLACEHOLDER] Ideal screenshot: random student selector with discussion prompt, sentence stem, and no-repeat round status.",
      gif: "[GIF PLACEHOLDER] Ideal GIF: think timer finishes, randomizer selects a student, then marks the student as completed.",
      video: "[VIDEO PLACEHOLDER] Ideal video: participation routine showing wait time, fair selection, and a supportive response option.",
    },
    faqs: [
      {
        question: "What is a student participation randomizer?",
        answer:
          "It is a tool that randomly selects students for discussion, turns, roles, or responses to help participation spread more fairly.",
      },
      {
        question: "How do I make random participation less stressful?",
        answer:
          "Give wait time, let students write first, use sentence stems, and allow supportive options like sharing a partner's idea.",
      },
      {
        question: "Is random selection better than calling on volunteers?",
        answer:
          "It depends on the purpose. Random selection broadens voice, while volunteers can be useful for open discussion or sensitive questions.",
      },
      {
        question: "Can randomizers prevent repeat participation?",
        answer:
          "Many randomizers can run no-repeat rounds so every student has a turn before names reset.",
      },
      {
        question: "How can Lesson Launchpad support participation routines?",
        answer:
          "Lesson Launchpad can display prompts, start think-time timers, support participation tools, and move the lesson forward consistently.",
      },
    ],
    relatedLinks: [
      { to: "/wheel-of-names-for-teachers", label: "Wheel of Names for Teachers" },
      { to: "/random-group-generator", label: "Random Group Generator" },
      { to: "/daily-agenda-template-for-teachers", label: "Daily Agenda Template" },
      AUTOMATION_LINK,
    ],
  },
  "/classroom-schedule-display": {
    slug: "classroom-schedule-display",
    h1: "Classroom Schedule Display for Predictable, Calmer School Days",
    metaTitle: "Classroom Schedule Display | Lesson Launchpad™",
    metaDescription:
      "Use a classroom schedule display to increase predictability, reduce repeated questions, and smooth daily transitions.",
    primaryKeyword: "classroom schedule display",
    secondaryKeywords: ["classroom daily schedule", "visual classroom schedule"],
    longTailKeywords: ["daily classroom schedule display", "visual schedule for classroom routines"],
    hero: {
      eyebrow: "Predictability and Transitions",
      value:
        "A visible schedule helps students feel oriented. They can see what is happening now, what comes next, and when the next transition will happen.",
      cta: "Display the day clearly",
    },
    problem: {
      headline: "Students ask fewer questions when the day is visible.",
      paragraphs: [
        "What are we doing next? When is lunch? Are we still having groups? Did the assembly change the schedule? These questions are understandable, especially when the day changes.",
        "A classroom schedule display reduces uncertainty. It supports students who need predictability and helps the whole class transition with less teacher narration.",
      ],
    },
    why: {
      headline: "Why schedules are powerful classroom management tools",
      items: [
        "Predictability reduces anxiety for many students.",
        "A visible schedule gives students a shared reference point.",
        "Schedule changes are less disruptive when they are displayed clearly.",
        "Transitions improve when students can anticipate what comes next.",
      ],
    },
    traditional: {
      headline: "Traditional schedules can become stale",
      items: [
        "A printed schedule is stable, but it may not reflect assemblies, testing, or early release days.",
        "A board schedule works well if it is updated consistently.",
        "Slides can show the day, but they require manual changes between classes or periods.",
      ],
    },
    better: {
      headline: "A better schedule display changes with the day",
      paragraphs: [
        "A strong classroom daily schedule highlights the current block, shows what comes next, and connects schedule moments to timers and reminders.",
        "When the display updates automatically, students do not have to rely on the teacher's memory or a static plan from the morning.",
      ],
    },
    features: [
      "Show the current class period, activity, or daily block.",
      "Display upcoming transitions before they happen.",
      "Pair schedule items with agenda content, timers, and reminders.",
      "Support different schedules for different classes or days.",
    ],
    tips: [
      "Highlight 'now' and 'next' so the schedule is easy to scan.",
      "Use simple labels students understand from your routines.",
      "Update schedule changes visibly instead of only announcing them.",
      "Pair schedule transitions with a countdown or sound cue.",
      "Use the schedule as a calm redirect: 'Check now and next.'",
    ],
    connection: [
      [
        "A classroom schedule display creates predictability. Lesson Launchpad can connect that schedule to automation, so class changes, agendas, countdowns, and reminders update as the day moves.",
      ],
      [
        "For a full structure system, pair this with the ",
        { text: "Daily Agenda Template", to: "/daily-agenda-template-for-teachers" },
        ", ",
        { text: "Classroom Routine Builder", to: "/classroom-routine-builder" },
        ", and ",
        { text: "Classroom Transition Timer", to: "/classroom-transition-timer" },
        ".",
      ],
    ],
    media: {
      screenshot: "[SCREENSHOT PLACEHOLDER] Ideal screenshot: visual classroom schedule with now, next, time remaining, and upcoming transition.",
      gif: "[GIF PLACEHOLDER] Ideal GIF: schedule advances from one class period to the next and updates the agenda automatically.",
      video: "[VIDEO PLACEHOLDER] Ideal video: daily schedule display reducing repeated questions during a changed or shortened schedule.",
    },
    faqs: [
      {
        question: "What is a classroom schedule display?",
        answer:
          "A classroom schedule display is a visual schedule shown on a board, projector, or classroom screen so students can see the plan for the day.",
      },
      {
        question: "How does a visual classroom schedule help students?",
        answer:
          "It increases predictability, reduces repeated questions, and helps students prepare for transitions and changes.",
      },
      {
        question: "What should a classroom daily schedule show?",
        answer:
          "Show what is happening now, what comes next, important times, schedule changes, and any materials or reminders students need.",
      },
      {
        question: "Are classroom schedules only for elementary students?",
        answer:
          "No. Middle and high school students also benefit from visible schedules, especially during block days, testing, assemblies, and multi-step lessons.",
      },
      {
        question: "Can Lesson Launchpad update schedules automatically?",
        answer:
          "Yes. Lesson Launchpad can update class periods, agendas, timers, and reminders based on the schedule you set.",
      },
    ],
    relatedLinks: [
      { to: "/daily-agenda-template-for-teachers", label: "Daily Agenda Template" },
      { to: "/classroom-routine-builder", label: "Classroom Routine Builder" },
      { to: "/bell-ringer-timer", label: "Bell Ringer Timer" },
      AUTOMATION_LINK,
    ],
  },
};

export const CLASSROOM_TOOL_CARDS = CLASSROOM_TOOL_PAGE_PATHS.map((path) => {
  const page = CLASSROOM_TOOL_PAGES[path];
  return {
    to: path,
    title: page.h1.replace(/ for .*/, ""),
    description: page.hero.value,
  };
});
